import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { logOff, sessionCache } from "modules/auth";

const cache = sessionCache();
const AD_IDLE_LOGOUT_MS = 6 * 60 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "adLastActivityAt";
const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;

export const useAdIdleLogout = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        let lastActivityWriteAt = 0;

        const updateLastActivity = () => {
            const now = Date.now();
            if (now - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;

            lastActivityWriteAt = now;
            localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, now.toString());
        };

        const getLastActivity = (): number => {
            const value = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
            const parsed = Number(value);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : Date.now();
        };

        const checkAdInactivity = () => {
            const session = cache.getSession();
            if (!session?.isAdLogin) return;

            const inactiveForMs = Date.now() - getLastActivity();
            if (inactiveForMs >= AD_IDLE_LOGOUT_MS) {
                localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
                dispatch(logOff());
            }
        };

        const initializeTracking = () => {
            const session = cache.getSession();
            if (session?.isAdLogin && !localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) {
                updateLastActivity();
            }
        };

        const activityEvents: Array<keyof WindowEventMap> = [
            "click",
            "mousemove",
            "keydown",
            "scroll",
            "touchstart",
        ];

        initializeTracking();
        activityEvents.forEach(eventName => window.addEventListener(eventName, updateLastActivity, { passive: true }));

        const interval = window.setInterval(checkAdInactivity, 60 * 1000);
        checkAdInactivity();

        return () => {
            activityEvents.forEach(eventName => window.removeEventListener(eventName, updateLastActivity));
            window.clearInterval(interval);
        };
    }, [dispatch]);
};
